import React from 'react'
import { connect } from 'react-redux'
import { t } from 'ttag'
import * as R from 'ramda'
import {
  getRespondent,
  getGMA,
  getPersonality,
  getInterest,
  reuseConsent,
} from 'ducks/respondent'
import Link from 'redux-first-router-link'
import cx from 'classnames'
import {
  getFirstUnansweredPersonality,
  getFirstUnansweredInterest,
} from 'sagas/pagingSaga'
import {
  toTerms,
  toGmaQuiz,
  toGmaQuizWelcome,
  toPersonalityQuiz,
  toInterestQuiz,
} from 'ducks/router'
import Mail from 'assets/icons/mail.svg'
import styles from './Quiz.module.scss'

import warningImg from 'assets/icons/warning.png'

import { Card } from '../Card/Card'
import { WaveContainer } from '../WaveContainer/WaveContainer'
import { Loader } from 'components/shared/Loader/Loader'
import { Link as DownloadLink } from 'components/shared/Link/Link'
import { DownloadIcon } from 'components/shared/Icon/model'

import DotsSvg from 'assets/icons/dots.svg'
import TargetSvg from 'assets/icons/target.svg'
import ArrowSvg from 'assets/icons/arrow.svg'

const getCardButtonLabel = status => {
  return {
    completed: t`Test results`,
    created: t`Go to test`,
    started: t`Continue test`,
  }[status]
}

const GMACard = connect(getGMA, { toGmaQuiz, toGmaQuizWelcome, reuseConsent })(
  ({
    uuid,
    status,
    isOptional,
    toGmaQuiz,
    toGmaQuizWelcome,
    reuseConsent,
    showReuseButton,
  }) => {
    return (
      <Card
        image={TargetSvg}
        name={t`Ability Test`}
        isOptional={isOptional}
        description={t`An inductive reasoning test measures your ability to identify patterns and draw logical conclusions.​`}
        buttonLabel={getCardButtonLabel(status)}
        completed={status === 'completed'}
        onClick={() =>
          status === 'created'
            ? toGmaQuizWelcome({ uuid })
            : toGmaQuiz({ uuid })
        }
        showReuseButton={showReuseButton}
        handleReuse={() => reuseConsent(true, ['gma'])}
      />
    )
  },
)

export const MailHint = () => {
  return (
    <div className={styles.hint}>
      <img src={Mail} />
      <span>
        {t`If you have any questions do not hesitate to contact us at`}{' '}
        <a className={styles.link} href="mailto:ebber@ebber.com" key="email">
          ebber@ebber.com
        </a>
      </span>
    </div>
  )
}

const TermsHint = ({ uuid }) => {
  return (
    <div className={styles.hint}>
      <span>
        {t`By commencing the tests, you accept the`}{' '}
        <Link
          className={styles.link}
          to={toTerms({ uuid })}
        >{t`terms of use and privacy`}</Link>
      </span>
    </div>
  )
}

const PersonalityCard = connect(getPersonality, {
  toPersonalityQuiz,
  reuseConsent,
})(({
  uuid,
  toPersonalityQuiz,
  status,
  answers,
  questions,
  showReuseButton,
  reuseConsent,
}) => {
  const nextQuestion = questions
    ? getFirstUnansweredPersonality({ answers, questions })(0)
    : undefined

  return (
    <Card
      image={DotsSvg}
      name={t`Personality Test`}
      description={t`A personality test reveals your strengths and work style to enhance self-awareness.`}
      buttonLabel={getCardButtonLabel(status)}
      completed={status === 'completed'}
      onClick={() => toPersonalityQuiz({ uuid, page: nextQuestion })}
      showReuseButton={showReuseButton}
      handleReuse={() => reuseConsent(true, ['personality'])}
    />
  )
})

const InterestCard = connect(getInterest, {
  toInterestQuiz,
})(({ uuid, toInterestQuiz, status, answers, questions }) => {
  const isCompleted = status === 'completed'
  const nextQuestion = questions
    ? getFirstUnansweredInterest({ answers, questions })(0)
    : undefined

  return (
    <Card
      image={ArrowSvg}
      name={t`Interest Test`}
      description={t`An interest test reveals which talent areas you are most interested in and will thrive in.`}
      buttonLabel={getCardButtonLabel(status)}
      completed={isCompleted}
      onClick={() => toInterestQuiz({ uuid, page: nextQuestion })}
      hideButton={isCompleted}
    />
  )
})

const DownloadResults = ({ pdf, language, personality, gma }) => {
  const canDownload =
    personality?.status === 'completed' &&
    gma?.status === 'completed' &&
    Boolean(pdf)

  if (!canDownload) return null

  const link = R.replace(/LC/g, language, pdf)
  const filename = link.includes('EBBER-report')
    ? link.substring(link.indexOf('EBBER-report'))
    : 'EBBER-report.pdf'

  return (
    <div className={styles.downloadResults}>
      <DownloadLink
        icon={DownloadIcon}
        label={t`Download results`}
        size="medium"
        to={link}
        isExternal
        filename={filename}
        color="primary"
        variant="filled"
      />
    </div>
  )
}

const Quiz = ({
  name,
  id: uuid,
  status,
  loading,
  personality,
  gma,
  interest,
  is_job_seeker,
  has_previous_results: hasPreviousResults,
  reuse_selection: reuseSelection,
  pdf,
  language,
}) => {
  const applicationFinished = is_job_seeker
    ? personality.status === 'completed' && interest.status === 'completed'
    : status === 'completed'

  return (
    <WaveContainer>
      <div className={styles.centerContent}>
        {loading ? (
          <Loader isInline isFullScreen />
        ) : uuid ? (
          <>
            <div className={styles.title}>
              {applicationFinished ? (
                <span>{t`Congratulations`}!</span>
              ) : (
                <>
                  <span>{`${t`Welcome`}, `}</span>
                  <span className={styles.name}>{name}</span>
                </>
              )}
            </div>
            <div
              className={cx(styles.subtitle, {
                [styles.finished]: applicationFinished,
              })}
            >
              {is_job_seeker
                ? applicationFinished
                  ? t`You have completed both required tests.`
                  : t`You need to take Personality and Interest tests to complete the process.`
                : applicationFinished
                  ? t`You have completed both tests. The recruitment manager will contact you to inform you about the next steps.`
                  : t`You need to take both tests to complete the process.`}
            </div>
            <div className={styles.cards}>
              {is_job_seeker && interest ? <InterestCard uuid={uuid} /> : null}
              {personality ? (
                <PersonalityCard
                  uuid={uuid}
                  showReuseButton={
                    personality.status !== 'completed' &&
                    hasPreviousResults?.['personality'] &&
                    !reuseSelection?.['personality']
                  }
                />
              ) : null}
              {gma ? (
                <GMACard
                  uuid={uuid}
                  isOptional={is_job_seeker}
                  showReuseButton={
                    gma.status !== 'completed' &&
                    hasPreviousResults?.['gma'] &&
                    !reuseSelection?.['gma']
                  }
                />
              ) : null}
            </div>
            <DownloadResults
              pdf={pdf}
              language={language}
              personality={personality}
              gma={gma}
            />
          </>
        ) : (
          <div className={styles.invalidLinkBox}>
            <span className={styles.invalidLinkIcon} aria-hidden>
              <img alt="warning" src={warningImg} />
            </span>
            <span className={styles.invalidLinkText}>
              {t`This test link appears to be incorrect. Please use the link from your invitation email or contact support at`}{' '}
              <a
                className={styles.invalidLinkEmail}
                href="mailto:ebber@ebber.com"
              >
                ebber@ebber.com
              </a>
              .
            </span>
          </div>
        )}
      </div>

      <div className={styles.hints}>
        {uuid ? <TermsHint uuid={uuid} /> : null}
        <MailHint />
      </div>
    </WaveContainer>
  )
}

export default connect(getRespondent)(Quiz)
